'use client';

import {
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  X,
  Images,
  Heart,
  Share,
  Printer,
} from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  Autoplay,
  type CarouselApi,
} from '@noirproperties/ui/components/ui/carousel';
import Image from 'next/image';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@noirproperties/ui/components/ui/button';
import Link from 'next/link';
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { DialogProvider } from '@/contexts/dialog-context';
import { DrawerDialog } from '@/components/client/drawer-dialog';
import { LeadInquiryForm } from '@/forms/lead';
import type { Agent } from '@/lib/content/types';
import { ImageOverlay } from './image-section.client';

function CompactAgentContact({
  agent,
  pageName,
  siteUrl,
}: {
  agent?: Agent;
  pageName: string;
  siteUrl: string;
}) {
  if (!agent) {
    return null;
  }

  return (
    <div className="flex w-full max-w-2xl items-center space-x-4 rounded-md bg-gunmetal/80 p-4 backdrop-blur-sm">
      <div className="flex-shrink-0">
        <Image
          alt={agent.name.full}
          className="h-24 w-24 rounded-md object-cover"
          height={96}
          src={agent.image}
          width={96}
        />
      </div>
      <div className="flex-grow">
        <Link className="hover:underline" href={`/agents/${agent.slug}`}>
          <h3 className="text-xl font-semibold text-white">
            {agent.name.full}
          </h3>
        </Link>
        <p className="text-sm text-gray-300">{agent.role}</p>
        <div className="mt-2">
          <DialogProvider>
            <DrawerDialog
              buttonContent="Contact agent"
              cancelButtonText="Cancel"
              description="We respond quickly to assist you with your real estate needs."
              title={'Get in touch with {{name}}'.replace(
                '{{name}}',
                agent.name.first,
              )}
            >
              <LeadInquiryForm pageName={pageName} siteUrl={siteUrl} />
            </DrawerDialog>
          </DialogProvider>
        </div>
      </div>
    </div>
  );
}

function GenericContact({
  pageName,
  siteUrl,
}: {
  pageName: string;
  siteUrl: string;
}) {
  return (
    <div className="flex w-full max-w-2xl items-center space-x-4 rounded-md bg-gunmetal/80 p-4 backdrop-blur-sm">
      <div className="flex-grow">
        <h3 className="text-xl font-semibold text-white">
          Interested in this property?
        </h3>
        <p className="text-sm text-gray-300">Get in touch with our team</p>
        <div className="mt-2">
          <DialogProvider>
            <DrawerDialog
              buttonContent="Contact us"
              cancelButtonText="Cancel"
              description="We're here to assist you with any questions about this property."
              title="Get more information"
            >
              <LeadInquiryForm pageName={pageName} siteUrl={siteUrl} />
            </DrawerDialog>
          </DialogProvider>
        </div>
      </div>
    </div>
  );
}

function QuickActions() {
  return (
    <div className="flex w-full max-w-md items-center justify-between space-x-4 rounded-md bg-gunmetal/80 p-4 backdrop-blur-sm">
      <Button
        className="flex h-20 flex-1 flex-col items-center justify-center space-y-2 text-white"
        variant="ghost"
      >
        <Heart className="h-8 w-8" />
        <span className="text-sm">Save</span>
      </Button>
      <Button
        className="flex h-20 flex-1 flex-col items-center justify-center space-y-2 text-white"
        variant="ghost"
      >
        <Share className="h-8 w-8" />
        <span className="text-sm">Share</span>
      </Button>
      <Button
        className="flex h-20 flex-1 flex-col items-center justify-center space-y-2 text-white"
        variant="ghost"
      >
        <Printer className="h-8 w-8" />
        <span className="text-sm">Print</span>
      </Button>
    </div>
  );
}

const PLACEHOLDER_SRC = `data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs%3D`;

function LazyLoadImage({
  image,
  inView,
}: {
  image: { id: string; url: string };
  inView: boolean;
}) {
  const [hasLoaded, setHasLoaded] = useState(false);

  const setLoaded = useCallback(() => {
    if (inView) setHasLoaded(true);
  }, [inView]);

  return (
    <div className="relative h-full w-full">
      {!hasLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gunmetal">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-linen border-t-transparent" />
        </div>
      )}
      <Image
        alt=""
        className={`object-cover transition-opacity duration-300 ${hasLoaded ? 'opacity-100' : 'opacity-0'}`}
        fill
        onLoad={setLoaded}
        quality={100}
        src={inView ? image.url : PLACEHOLDER_SRC}
      />
    </div>
  );
}

type ImageHeroProps = {
  text: {
    category: string;
    title: string;
    description: string;
  };
  content: React.ReactNode;
  images: { id: string; url: string }[];
  className?: string;
  agent?: Agent;
  pageName: string;
  siteUrl: string;
};

export function ImageHero({
  text,
  content,
  images,
  className,
  agent,
  pageName,
  siteUrl,
}: ImageHeroProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [slidesInView, setSlidesInView] = useState<number[]>([]);

  const updateCarouselState = useCallback(() => {
    if (!api) return;
    setCurrentSlide(api.selectedScrollSnap() + 1);
    setIsMinimized(true);
  }, [api]);

  const toggleAutoplay = useCallback(() => {
    if (!api) return;
    const autoplayPlugin = api.plugins().autoplay;
    autoplayPlugin.isPlaying() ? autoplayPlugin.stop() : autoplayPlugin.play();
  }, [api]);

  const stopAutoplay = useCallback(() => {
    if (!api) return;
    const autoplayPlugin = api.plugins().autoplay;
    if (autoplayPlugin.isPlaying()) {
      autoplayPlugin.stop();
    }
  }, [api]);

  const handleScroll = useCallback(
    (direction: 'prev' | 'next') => {
      if (!api) return;
      direction === 'prev' ? api.scrollPrev() : api.scrollNext();
      updateCarouselState();
      stopAutoplay();
    },
    [api, updateCarouselState, stopAutoplay],
  );

  const updateSlidesInView = useCallback(() => {
    if (!api) return;
    setSlidesInView((prevSlidesInView) => {
      const inView = api
        .slidesInView()
        .filter((index) => !prevSlidesInView.includes(index));
      return [...prevSlidesInView, ...inView];
    });
  }, [api]);

  useEffect(() => {
    if (!api) return;

    const onPlay = () => {
      setIsPlaying(true);
    };
    const onStop = () => {
      setIsPlaying(false);
    };

    api.on('select', updateCarouselState);
    api.on('autoplay:play', onPlay);
    api.on('autoplay:stop', onStop);
    api.on('slidesInView', updateSlidesInView);
    api.on('reInit', updateSlidesInView);

    updateSlidesInView();

    return () => {
      api.off('select', updateCarouselState);
      api.off('autoplay:play', onPlay);
      api.off('autoplay:stop', onStop);
      api.off('slidesInView', updateSlidesInView);
      api.off('reInit', updateSlidesInView);
    };
  }, [api, updateCarouselState, updateSlidesInView]);

  return (
    <div className="relative isolate">
      <ImageOverlay
        className={className}
        slots={{
          visible: (
            <div className="pointer-events-none relative z-20 flex min-h-[calc(100vh-4rem)] flex-col justify-end px-6 sm:justify-center sm:py-40 sm:pt-0 lg:px-8 lg:py-56 lg:pe-0 xl:justify-end xl:pb-96 xl:pt-0">
              <div className="absolute inset-x-0 bottom-0 top-[15%] bg-gradient-to-b from-black/30 via-black/15 to-transparent sm:hidden" />
              <div className="pointer-events-auto relative z-30 mx-auto mt-[10vh] max-w-2xl rounded-lg bg-black bg-opacity-50 p-6 backdrop-blur-sm transition-all duration-300 sm:mt-0 lg:mx-0 lg:max-w-2xl">
                <Button
                  className="absolute right-2 top-2 p-1.5 text-neutral-400 transition-colors duration-200 hover:bg-black/40 hover:text-white"
                  onClick={() => {
                    setIsMinimized(!isMinimized);
                  }}
                  size="lg"
                  variant="ghost"
                >
                  {isMinimized ? (
                    <X className="h-4 w-4" />
                  ) : (
                    <div className="flex items-center text-neutral-200">
                      <Images className="mr-1 h-4 w-4" />
                      <span className="text-xs font-medium">Images</span>
                    </div>
                  )}
                </Button>
                {isMinimized ? (
                  <div>
                    <span className="inline-block rounded-md bg-linen/80 px-2 py-1 text-xs font-medium uppercase tracking-tight text-black shadow-sm">
                      Images
                    </span>
                    <h2 className="mt-2 truncate pr-8 text-lg font-semibold text-linen">
                      {text.title}
                    </h2>
                    <div className="mt-2 flex flex-col items-start text-xs text-neutral-300">
                      <div className="mb-1 flex items-center space-x-1">
                        <Button
                          className="p-1 text-neutral-400 transition-colors duration-200 hover:bg-black/40 hover:text-white"
                          onClick={() => {
                            handleScroll('prev');
                          }}
                          size="icon"
                          variant="ghost"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          <span className="sr-only">Previous image</span>
                        </Button>
                        <Button
                          className="p-1 text-neutral-400 transition-colors duration-200 hover:bg-black/40 hover:text-white"
                          onClick={toggleAutoplay}
                          size="icon"
                          variant="ghost"
                        >
                          {isPlaying ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                          <span className="sr-only">
                            {isPlaying ? 'Pause autoplay' : 'Start autoplay'}
                          </span>
                        </Button>
                        <Button
                          className="p-1 text-neutral-400 transition-colors duration-200 hover:bg-black/40 hover:text-white"
                          onClick={() => {
                            handleScroll('next');
                          }}
                          size="icon"
                          variant="ghost"
                        >
                          <ChevronRight className="h-4 w-4" />
                          <span className="sr-only">Next image</span>
                        </Button>
                      </div>
                      <span>
                        Image {currentSlide} of {images.length}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="mb-2 inline-block rounded-md bg-linen/80 px-3 py-1.5 text-xs font-medium uppercase tracking-tight text-black shadow-sm">
                      {text.category}
                    </span>
                    <h1 className="text-xl font-bold tracking-tight text-linen sm:text-4xl md:text-4xl lg:text-4xl xl:text-2xl">
                      {text.title}
                    </h1>
                    <div className="mt-4">
                      <p className="text-base leading-7 text-neutral-200 sm:text-lg">
                        {text.description}
                      </p>
                      <div className="mt-6">{content}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ),
        }}
      >
        <Carousel
          className="absolute inset-0 z-10 h-full w-full overflow-hidden bg-gunmetal"
          opts={{
            align: 'start',
            loop: true,
          }}
          plugins={[
            Autoplay({
              delay: 3000,
              playOnInit: false,
              stopOnInteraction: true,
              stopOnLastSnap: true,
            }),
          ]}
          setApi={setApi}
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem
                className="h-screen max-h-[45vh] sm:max-h-[60vh] md:max-h-[60vh] xl:max-h-[80vh]"
                key={image.id}
              >
                <LazyLoadImage
                  image={image}
                  inView={slidesInView.includes(index)}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-between p-4">
            {(['prev', 'next'] as const).map((direction) => (
              <Button
                className="pointer-events-auto bg-black/50 p-2 text-white transition-colors duration-200 hover:bg-black/75 hover:text-white"
                key={direction}
                onClick={() => {
                  handleScroll(direction);
                }}
                size="icon"
                variant="ghost"
              >
                {direction === 'prev' ? (
                  <ArrowLeftIcon className="h-6 w-6" />
                ) : (
                  <ArrowRightIcon className="h-6 w-6" />
                )}
                <span className="sr-only">
                  {direction === 'prev' ? 'Previous' : 'Next'} slide
                </span>
              </Button>
            ))}
          </div>
        </Carousel>
      </ImageOverlay>
      <div className="absolute bottom-0 left-0 right-0 z-40 flex flex-col space-y-4 px-6 pb-4 lg:px-8">
        <div className="flex w-full items-end justify-between space-x-4">
          <div className="flex-grow">
            {agent ? (
              <CompactAgentContact
                agent={agent}
                pageName={pageName}
                siteUrl={siteUrl}
              />
            ) : (
              <GenericContact pageName={pageName} siteUrl={siteUrl} />
            )}
          </div>
          {/* TODO: Currently hidden until implemented. Do not remove this comment */}
          <div className="hidden flex-shrink-0">
            <QuickActions />
          </div>
        </div>
      </div>
    </div>
  );
}
